// ─── Timezone helpers ─────────────────────────────────────────────────────────
// Bridges the customer's stored timezone (Account.timezone — which may be an
// IANA id like "America/New_York" or a verbose Windows-style label like
// "(UTC-05:00) Eastern Time (US & Canada)") to IANA ids, and converts between
// UTC instants and wall-clock parts in that zone using only the Intl API
// (no date-fns-tz dependency).
// ──────────────────────────────────────────────────────────────────────────────

// Common verbose / Windows-style labels → IANA. Substring match (case-insensitive).
//
// IMPORTANT: Do NOT use 'utc' or 'gmt' as bare keys — every Windows-style
// timezone label starts with "(UTC±HH:MM)" so 'utc' would match ALL of them.
// Use 'utc)' for the bare "(UTC)" label only.
// For "(UTC+00:00) UTC" use 'utc+00'; for "(UTC-12:00)..." use 'international date line'.
const WINDOWS_TZ = {
  // ── UTC-12 ─────────────────────────────────────────────────────────────────
  'international date line': 'Etc/GMT+12',
  'dateline':                'Etc/GMT+12',
  // ── North America ──────────────────────────────────────────────────────────
  'eastern time':       'America/New_York',
  'central time':       'America/Chicago',
  'mountain time':      'America/Denver',
  'arizona':            'America/Phoenix',
  'pacific time':       'America/Los_Angeles',
  'alaska':             'America/Anchorage',
  'hawaii':             'Pacific/Honolulu',
  'atlantic time':      'America/Halifax',
  'newfoundland':       'America/St_Johns',
  'mexico city':        'America/Mexico_City',
  'central america':    'America/Guatemala',
  'bogota':             'America/Bogota',
  'caracas':            'America/Caracas',
  'santiago':           'America/Santiago',
  'brasilia':           'America/Sao_Paulo',
  'buenos aires':       'America/Argentina/Buenos_Aires',
  // ── Europe ─────────────────────────────────────────────────────────────────
  'london':             'Europe/London',
  'dublin':             'Europe/London',
  'lisbon':             'Europe/Lisbon',
  'central european':   'Europe/Paris',
  'paris':              'Europe/Paris',
  'berlin':             'Europe/Berlin',
  'amsterdam':          'Europe/Amsterdam',
  'brussels':           'Europe/Brussels',
  'madrid':             'Europe/Madrid',
  'rome':               'Europe/Rome',
  'vienna':             'Europe/Vienna',
  'warsaw':             'Europe/Warsaw',
  'prague':             'Europe/Prague',
  'budapest':           'Europe/Budapest',
  'eastern european':   'Europe/Helsinki',
  'helsinki':           'Europe/Helsinki',
  'athens':             'Europe/Athens',
  'bucharest':          'Europe/Bucharest',
  'istanbul':           'Europe/Istanbul',
  'moscow':             'Europe/Moscow',
  'minsk':              'Europe/Minsk',
  // ── Middle East / Africa ───────────────────────────────────────────────────
  'riyadh':             'Asia/Riyadh',
  'dubai':              'Asia/Dubai',
  'abu dhabi':          'Asia/Dubai',
  'muscat':             'Asia/Muscat',
  'tehran':             'Asia/Tehran',
  'kabul':              'Asia/Kabul',
  'cairo':              'Africa/Cairo',
  'nairobi':            'Africa/Nairobi',
  'johannesburg':       'Africa/Johannesburg',
  'casablanca':         'Africa/Casablanca',
  // ── South / Central Asia ───────────────────────────────────────────────────
  'karachi':            'Asia/Karachi',
  'tashkent':           'Asia/Tashkent',
  // India (+05:30) — all common Windows label variants for this offset
  'india':              'Asia/Kolkata',
  'kolkata':            'Asia/Kolkata',
  'calcutta':           'Asia/Kolkata',
  'chennai':            'Asia/Kolkata',
  'mumbai':             'Asia/Kolkata',
  'new delhi':          'Asia/Kolkata',
  'sri lanka':          'Asia/Colombo',
  'kathmandu':          'Asia/Kathmandu',
  'dhaka':              'Asia/Dhaka',
  'almaty':             'Asia/Almaty',
  // ── East / Southeast Asia ──────────────────────────────────────────────────
  'rangoon':            'Asia/Rangoon',
  'yangon':             'Asia/Yangon',
  'bangkok':            'Asia/Bangkok',
  'hanoi':              'Asia/Bangkok',
  'jakarta':            'Asia/Jakarta',
  'beijing':            'Asia/Shanghai',
  'chongqing':          'Asia/Shanghai',
  'china standard':     'Asia/Shanghai',
  'hong kong':          'Asia/Hong_Kong',
  'taipei':             'Asia/Taipei',
  'singapore':          'Asia/Singapore',
  'kuala lumpur':       'Asia/Kuala_Lumpur',
  'perth':              'Australia/Perth',
  'seoul':              'Asia/Seoul',
  'korea standard':     'Asia/Seoul',
  'osaka':              'Asia/Tokyo',
  'sapporo':            'Asia/Tokyo',
  'tokyo':              'Asia/Tokyo',
  'japan standard':     'Asia/Tokyo',
  'yakutsk':            'Asia/Yakutsk',
  // ── Pacific / Australia ────────────────────────────────────────────────────
  'darwin':             'Australia/Darwin',
  'adelaide':           'Australia/Adelaide',
  'canberra':           'Australia/Sydney',
  'melbourne':          'Australia/Melbourne',
  'sydney':             'Australia/Sydney',
  'brisbane':           'Australia/Brisbane',
  'hobart':             'Australia/Hobart',
  'vladivostok':        'Asia/Vladivostok',
  'auckland':           'Pacific/Auckland',
  'new zealand':        'Pacific/Auckland',
  'fiji':               'Pacific/Fiji',
  'samoa':              'Pacific/Apia',
  // ── UTC / GMT ───────────────────────────────────────────────────────────────
  // 'utc)' matches "(UTC)" bare label; 'utc+00' matches "(UTC+00:00) UTC"
  // DO NOT use bare 'utc' or 'gmt' — they match ALL Windows-style timezone strings.
  'utc+00':             'UTC',
  'utc)':               'UTC',
  'gmt)':               'UTC',
  'coordinated universal': 'UTC',
}

function isValidIana(tz) {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz })
    return true
  } catch {
    return false
  }
}

/** Resolve any stored timezone string to a usable IANA id (falls back to UTC). */
export function resolveIanaTz(rawTz) {
  if (!rawTz || typeof rawTz !== 'string') return 'UTC'
  const t = rawTz.trim()
  // 1. Already a valid IANA id (e.g. "America/New_York")
  if (isValidIana(t)) return t
  // 2. Windows-style label lookup via substring
  const lower = t.toLowerCase()
  for (const [needle, iana] of Object.entries(WINDOWS_TZ)) {
    if (lower.includes(needle)) return iana
  }
  // 3. Parse "(UTC±HH:MM)" prefix and map to Etc/GMT for whole-hour offsets.
  //    This is a catch-all for any Windows-style label not in the map above.
  //    Note: Etc/GMT sign convention is inverted (Etc/GMT+12 = UTC-12:00).
  const m = t.match(/\(UTC([+-])(\d{1,2}):(\d{2})\)/)
  if (m) {
    const sign = m[1], hours = parseInt(m[2], 10), mins = parseInt(m[3], 10)
    if (hours === 0 && mins === 0) return 'UTC'
    if (mins === 0) {
      const etcSign = sign === '+' ? '-' : '+'
      const candidate = `Etc/GMT${etcSign}${hours}`
      if (isValidIana(candidate)) return candidate
    }
  }
  // 4. Unknown — default to UTC (not browser TZ: server locale != user's timezone)
  return 'UTC'
}

/**
 * Format a media timestamp for display: "08/12/2026 13:50 EDT".
 *
 * `rawTz` is the account's stored timezone, in either form — an IANA id or a
 * Windows-style label like "(UTC-05:00) Eastern Time (US & Canada)".
 * resolveIanaTz() bridges both, and Intl then applies the offset that is
 * actually in force at that instant, so daylight saving is correct.
 *
 * This is why formatting happens here rather than server-side: reading the
 * literal "-05:00" out of that label gives a FIXED offset, which is an hour
 * wrong for Eastern from March to November (EDT is UTC-04:00) — and it prints a
 * bare number, which doesn't tell the reader which zone they're looking at.
 * The zone abbreviation is appended so the answer is explicit.
 */
export function formatMediaTimestamp(instant, rawTz) {
  if (instant == null || instant === '') return ''
  const date = instant instanceof Date ? instant : new Date(instant)
  if (isNaN(date.getTime())) return ''
  const tz = resolveIanaTz(rawTz)
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      timeZoneName: 'short',
    }).formatToParts(date)
    const get = (type) => (parts.find((p) => p.type === type) || {}).value || ''
    let hour = get('hour')
    if (hour === '24') hour = '00'   // h23 can emit 24 at midnight in some engines
    // Zones with a well-known abbreviation come back as "EDT"/"EST"/"PST".
    // Those without one come back as "GMT+5:30" — normalise that to the
    // "UTC+05:30" form the rest of the product uses.
    const zone = (get('timeZoneName') || '').replace(
      /^GMT([+-])(\d{1,2})(?::(\d{2}))?$/,
      (_, sign, h, m) => `UTC${sign}${String(h).padStart(2, '0')}:${m || '00'}`,
    )
    const stamp = `${get('month')}/${get('day')}/${get('year')} ${hour}:${get('minute')}`
    return zone ? `${stamp} ${zone}` : stamp
  } catch {
    return ''
  }
}

/**
 * Tick formatter for a time X-axis, chosen from how much time the chart covers.
 *
 * A fixed HH:mm formatter is only right for a single day. Over a month it prints
 * a clock time on every tick — "07:20  06:50  06:20 …" — which says nothing
 * about which day a point belongs to and reads as though the axis runs
 * backwards, because consecutive ticks land on different days.
 *
  *   up to ~2 days    → 14:30      (time of day)
 *   up to ~3 months  → Jul 8      (date)
 *   beyond that      → Jul 2026   (month + year)
 *
 * The three-month boundary matters: over a year, "Jul 8" style labels repeat
 * the same text at both ends of the axis (Aug 5 … Aug 5, twelve months apart),
 * so anything longer carries the year instead.
 */
export function makeTimeAxisFormatter(spanMs, tz) {
  const DAY = 86_400_000
  const span = Number(spanMs) || 0
  const opts =
    span <= 2 * DAY  ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
    : span <= 90 * DAY ? { month: 'short', day: 'numeric' }
    : { month: 'short', year: 'numeric' }
  let dtf
  try {
    dtf = new Intl.DateTimeFormat('en-US', { timeZone: tz || 'UTC', ...opts })
  } catch {
    dtf = new Intl.DateTimeFormat('en-US', opts)
  }
  return (value) => {
    if (!value) return ''
    const d = value instanceof Date ? value : new Date(value)
    return isNaN(d.getTime()) ? '' : dtf.format(d)
  }
}

/** Wall-clock parts (year, month 1-12, day, hour 0-23, minute, second) of a UTC instant in `tz`. */
export function wallClockPartsInTz(instant, tz) {
  const date = instant instanceof Date ? instant : new Date(instant)
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz || 'UTC',
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
  const parts = {}
  for (const p of dtf.formatToParts(date)) {
    if (p.type !== 'literal') parts[p.type] = parseInt(p.value, 10)
  }
  // h23 can emit hour 24 at midnight in some engines — normalise to 0.
  if (parts.hour === 24) parts.hour = 0
  return {
    year: parts.year, month: parts.month, day: parts.day,
    hour: parts.hour, minute: parts.minute, second: parts.second,
  }
}

// Offset (ms) of `tz` at the given UTC instant: (wall-clock-as-UTC) − utc.
function tzOffsetMs(utcMs, tz) {
  const p = wallClockPartsInTz(new Date(utcMs), tz)
  const asIfUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asIfUtc - utcMs
}

/**
 * Monthly marks anchored to the START of a window rather than to calendar
 * month starts: a range beginning 14 Aug gives 14 Sep, 14 Oct, … 14 Aug.
 *
 * The first mark is one month in (the window start itself sits on the axis
 * edge, where a label would be clipped). Day-of-month is clamped for shorter
 * months, so a 31 Jan window steps to 28/29 Feb rather than skipping it.
 */
export function monthlyTicksFrom(fromMs, toMs, tz) {
  const out = []
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return out
  const zone = tz || 'UTC'
  const pad = (n) => String(n).padStart(2, '0')
  const p = wallClockPartsInTz(new Date(fromMs), zone)
  for (let i = 1; i <= 400; i++) {
    const total = (p.month - 1) + i
    const year = p.year + Math.floor(total / 12)
    const month = (total % 12) + 1
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
    const day = Math.min(p.day, daysInMonth)
    const ms = parseWallClockInTzToMs(
      `${year}-${pad(month)}-${pad(day)} ${pad(p.hour)}:${pad(p.minute)}`, zone,
    )
    if (ms == null || ms > toMs) break
    out.push(ms)
  }
  return out
}

/**
 * Day labels for a tick list: "14 Sep" by default, "Sep 14" with
 * `{ order: 'month-day' }`.
 */
export function dayMonthTickLabels(tickMs, tz, { order = 'day-month' } = {}) {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const labels = new Map()
  for (const ms of tickMs) {
    const p = wallClockPartsInTz(new Date(ms), tz || 'UTC')
    const mon = MONTHS[p.month - 1]
    labels.set(ms, order === 'month-day' ? `${mon} ${p.day}` : `${p.day} ${mon}`)
  }
  return labels
}

/**
 * Local midnights across a window, thinned to at most `maxTicks` by stepping
 * whole days — so every label is a distinct date, evenly spaced.
 *
 * Without this, a multi-week range is left to the chart library, which places
 * ticks at even pixel intervals rather than on day boundaries. With date-only
 * labels that shows the same date twice in a row ("Aug 1  Aug 1 … Aug 8  Aug 8")
 * while skipping others entirely (Aug 11), which reads as a gap in the axis.
 */
export function dailyTicksInRange(fromMs, toMs, tz, { maxTicks = 15 } = {}) {
  const out = []
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return out
  const zone = tz || 'UTC'
  const pad = (n) => String(n).padStart(2, '0')
  const totalDays = Math.ceil((toMs - fromMs) / 86_400_000)
  const step = Math.max(1, Math.ceil(totalDays / maxTicks))

  // First local midnight at or after `fromMs`.
  const p = wallClockPartsInTz(new Date(fromMs), zone)
  let cursor = Date.UTC(p.year, p.month - 1, p.day)
  if (parseWallClockInTzToMs(`${p.year}-${pad(p.month)}-${pad(p.day)} 00:00`, zone) < fromMs) {
    cursor = Date.UTC(p.year, p.month - 1, p.day + 1)
  }
  for (let i = 0; i < 400; i++) {
    const d = new Date(cursor)
    const ms = parseWallClockInTzToMs(
      `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} 00:00`, zone,
    )
    if (ms == null || ms > toMs) break
    out.push(ms)
    cursor = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + step)
  }
  return out
}

/**
 * UTC instants of every midnight and midday (00:00 / 12:00 local) between two
 * instants, measured in `tz` — the tick positions a week-long axis wants.
 *
 * Letting the chart library pick its own positions over a week lands ticks
 * roughly every 12h, and with date-only labels each date then prints twice
 * ("Aug 8  Aug 8  Aug 9  Aug 9 …"). Pairing explicit half-day ticks with
 * halfDayTickLabels() gives the date at midnight and "12:00" at noon instead.
 */
export function halfDayTicksInRange(fromMs, toMs, tz) {
  const out = []
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return out
  const zone = tz || 'UTC'
  const p = wallClockPartsInTz(new Date(fromMs), zone)
  let cursor = Date.UTC(p.year, p.month - 1, p.day)   // civil date, stepped below
  // 60 days of half-days is a generous cap; it only guards a runaway range.
  for (let i = 0; i < 120; i++) {
    const d = new Date(cursor)
    const stamp = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
    for (const hh of ['00:00', '12:00']) {
      const ms = parseWallClockInTzToMs(`${stamp} ${hh}`, zone)
      if (ms == null) continue
      if (ms >= fromMs && ms <= toMs) out.push(ms)
    }
    cursor = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1)
    if (parseWallClockInTzToMs(`${new Date(cursor).toISOString().slice(0, 10)} 00:00`, zone) > toMs) break
  }
  return out.sort((a, b) => a - b)
}

/** Labels for halfDayTicksInRange(): the date at midnight, "12:00" at midday. */
export function halfDayTickLabels(tickMs, tz) {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const labels = new Map()
  for (const ms of tickMs) {
    const p = wallClockPartsInTz(new Date(ms), tz || 'UTC')
    labels.set(ms, p.hour === 0 ? `${MONTHS[p.month - 1]} ${p.day}` : '12:00')
  }
  return labels
}

/**
 * UTC instants of every even 2-hour boundary (00:00, 02:00, … 22:00 local)
 * between two instants, measured in `tz` — the tick positions a single-day
 * axis wants, matching the dashboard graphs' Day view (2-hour ticks).
 */
export function twoHourTicksInRange(fromMs, toMs, tz) {
  const out = []
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return out
  const zone = tz || 'UTC'
  const pad = (n) => String(n).padStart(2, '0')
  const p = wallClockPartsInTz(new Date(fromMs), zone)
  let cursor = Date.UTC(p.year, p.month - 1, p.day)   // civil date, stepped below
  for (let i = 0; i < 5; i++) {                        // a couple of days is plenty
    const d = new Date(cursor)
    const stamp = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
    for (let hh = 0; hh < 24; hh += 2) {
      const ms = parseWallClockInTzToMs(`${stamp} ${pad(hh)}:00`, zone)
      if (ms == null) continue
      if (ms >= fromMs && ms <= toMs) out.push(ms)
    }
    cursor = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 1)
    if (parseWallClockInTzToMs(`${new Date(cursor).toISOString().slice(0, 10)} 00:00`, zone) > toMs) break
  }
  return out.sort((a, b) => a - b)
}

/** Labels for twoHourTicksInRange(): the date at midnight, else "HH:MM". */
export function twoHourTickLabels(tickMs, tz) {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const pad = (n) => String(n).padStart(2, '0')
  const labels = new Map()
  for (const ms of tickMs) {
    const p = wallClockPartsInTz(new Date(ms), tz || 'UTC')
    labels.set(ms, (p.hour === 0 && p.minute === 0)
      ? `${MONTHS[p.month - 1]} ${p.day}`
      : `${pad(p.hour)}:${pad(p.minute)}`)
  }
  return labels
}

/**
 * UTC instants of every month start (1st, 00:00 local) between two instants,
 * measured in `tz`. Used to put one hash mark per month on a year-long axis
 * instead of leaving Recharts to pick arbitrary positions.
 *
 * A month start exactly at `fromMs` is included; one past `toMs` is not.
 */
export function monthStartsInRange(fromMs, toMs, tz) {
  const out = []
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return out
  const zone = tz || 'UTC'
  const p = wallClockPartsInTz(new Date(fromMs), zone)
  let year = p.year
  let month = p.month            // 1-12
  // 15 years of months is a generous cap; it only guards against a bad range.
  for (let i = 0; i < 180; i++) {
    const ms = parseWallClockInTzToMs(`${year}-${String(month).padStart(2, '0')}-01 00:00`, zone)
    if (ms == null) break
    if (ms > toMs) break
    if (ms >= fromMs) out.push(ms)
    month += 1
    if (month > 12) { month = 1; year += 1 }
  }
  return out
}

/**
 * Labels for monthStartsInRange() ticks: "Jan" normally, "Jan 2026" on the first
 * tick and whenever the year rolls over — so a range spanning two years never
 * shows the same month label twice without distinction.
 *
 * Past `maxLabels` months (a multi-year custom range) the intermediate labels
 * are blanked so the text stays readable — the hash marks themselves all remain,
 * because a tick with an empty label still draws its mark. Year rollovers are
 * always labelled.
 */
export function monthTickLabels(tickMs, tz, { maxLabels = 14 } = {}) {
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const labels = new Map()
  const step = Math.max(1, Math.ceil(tickMs.length / maxLabels))
  let prevYear = null
  tickMs.forEach((ms, i) => {
    const p = wallClockPartsInTz(new Date(ms), tz || 'UTC')
    const name = MONTHS[p.month - 1] || ''
    const yearChanged = p.year !== prevYear
    const keep = yearChanged || i % step === 0
    labels.set(ms, keep ? (yearChanged ? `${name} ${p.year}` : name) : '')
    prevYear = p.year
  })
  return labels
}

/** Parse a 'YYYY-MM-DD HH:MM[:SS]' wall-clock string interpreted in `tz` to a UTC ms timestamp. */
export function parseWallClockInTzToMs(str, tz) {
  if (!str) return null
  const m = String(str).match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/)
  if (!m) return null
  const [, Y, Mo, D, H, Mi, S] = m
  const asUtc = Date.UTC(+Y, +Mo - 1, +D, +H, +Mi, +(S || 0))
  // Correct by the zone offset at that instant; refine once for DST edges.
  let offset = tzOffsetMs(asUtc, tz)
  let ms = asUtc - offset
  const offset2 = tzOffsetMs(ms, tz)
  if (offset2 !== offset) ms = asUtc - offset2
  return ms
}
